/**
 * Center path square coordinates for pawn positioning across all RoadSections
 *
 * Each position maps to a specific square's x, y, width, height bounds
 * extracted from the white center path squares in the SVG.
 *
 * These coordinates are used by the renderPawnsAtPosition utility to
 * calculate proper pawn placement and centering.
 */

export interface CenterSquareBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Bottom Road Section (Green)
 * Layout: Bottom to top (GS1-GS6 left, GC1-GC6 center, GF1-GF6 right)
 */
export const BOTTOM_CENTER_SQUARE_BOUNDS = {
  // Center path positions (GC1-GC6) - middle column going up from bottom
  GC1: { x: 112.11, y: 476.54, width: 56.11, height: 48.05 }, // Row 6 bottom-most
  GC2: { x: 112.11, y: 399.7, width: 56.11, height: 48.05 }, // Row 5
  GC3: { x: 112.11, y: 322.86, width: 56.11, height: 48.05 }, // Row 4
  GC4: { x: 112.11, y: 246.02, width: 56.11, height: 48.05 }, // Row 3
  GC5: { x: 112.11, y: 169.18, width: 56.11, height: 48.05 }, // Row 2
  GC6: { x: 112.11, y: 92.34, width: 56.11, height: 48.05 }, // Row 1 top-most

  // Start road positions (GS1-GS6) - left column going up from bottom
  GS1: { x: 35.27, y: 476.54, width: 56.11, height: 48.05 }, // Row 6 bottom-most
  GS2: { x: 35.27, y: 399.7, width: 56.11, height: 48.05 }, // Row 5 (blank but mapped)
  GS3: { x: 35.27, y: 322.86, width: 56.11, height: 48.05 }, // Row 4
  GS4: { x: 35.27, y: 246.02, width: 56.11, height: 48.05 }, // Row 3
  GS5: { x: 35.27, y: 169.18, width: 56.11, height: 48.05 }, // Row 2
  GS6: { x: 35.27, y: 92.34, width: 56.11, height: 48.05 }, // Row 1 top-most

  // Final road positions (GF1-GF6) - right column going up from bottom
  GF1: { x: 189, y: 476.54, width: 56.11, height: 48.05 }, // Row 6 bottom-most
  GF2: { x: 189, y: 399.7, width: 56.11, height: 48.05 }, // Row 5
  GF3: { x: 189, y: 322.86, width: 56.11, height: 48.05 }, // Row 4
  GF4: { x: 189, y: 246.02, width: 56.11, height: 48.05 }, // Row 3
  GF5: { x: 189, y: 169.18, width: 56.11, height: 48.05 }, // Row 2
  GF6: { x: 189, y: 92.34, width: 56.11, height: 48.05 }, // Row 1 top-most

  // Final winning position (GFINAL) - arrow/triangle at top
  GFINAL: { x: 112.11, y: 18, width: 56.11, height: 48.05 } // Top triangle center
} as const;

/**
 * Top Road Section (Blue)
 * Layout: Top to bottom (BS1-BS6 right, BC1-BC6 center, BF1-BF6 left)
 */
export const TOP_CENTER_SQUARE_BOUNDS = {
  // Center path positions (BC1-BC6) - middle column, BC1 at TOP
  BC1: { x: 116.97, y: -3.66, width: 48.05, height: 48.05 }, // Row 1 top-most
  BC2: { x: 116.97, y: 73.18, width: 48.05, height: 48.05 }, // Row 2
  BC3: { x: 116.97, y: 150.02, width: 48.05, height: 48.05 }, // Row 3
  BC4: { x: 116.97, y: 226.86, width: 48.05, height: 48.05 }, // Row 4
  BC5: { x: 116.97, y: 303.7, width: 48.05, height: 48.05 }, // Row 5
  BC6: { x: 116.97, y: 380.54, width: 48.05, height: 48.05 }, // Row 6 bottom-most

  // Start road positions (BS1-BS6) - RIGHT column, BS1 at TOP
  BS1: { x: 193.81, y: -3.66, width: 48.05, height: 48.05 }, // Row 1 top-most right
  BS2: { x: 193.81, y: 73.18, width: 48.05, height: 48.05 }, // Row 2 right (blank but mapped)
  BS3: { x: 193.81, y: 150.02, width: 48.05, height: 48.05 }, // Row 3 right
  BS4: { x: 193.81, y: 226.86, width: 48.05, height: 48.05 }, // Row 4 right
  BS5: { x: 193.81, y: 303.7, width: 48.05, height: 48.05 }, // Row 5 right
  BS6: { x: 193.81, y: 380.54, width: 48.05, height: 48.05 }, // Row 6 bottom-most right

  // Final road positions (BF1-BF6) - LEFT column, BF1 at TOP
  BF1: { x: 40.13, y: -3.66, width: 48.05, height: 48.05 }, // Row 1 top-most left
  BF2: { x: 40.13, y: 73.18, width: 48.05, height: 48.05 }, // Row 2 left
  BF3: { x: 40.13, y: 150.02, width: 48.05, height: 48.05 }, // Row 3 left
  BF4: { x: 40.13, y: 226.86, width: 48.05, height: 48.05 }, // Row 4 left
  BF5: { x: 40.13, y: 303.7, width: 48.05, height: 48.05 }, // Row 5 left
  BF6: { x: 40.13, y: 380.54, width: 48.05, height: 48.05 }, // Row 6 bottom-most left

  // Final winning position (BFINAL) - arrow/triangle at bottom
  BFINAL: { x: 116.97, y: 464, width: 48.05, height: 48.05 } // Bottom triangle center
} as const;

/**
 * Right Road Section (Red)
 * Layout: RF (top), RC (middle), RS (bottom), right to left
 * Adjusted with x offset (+18) and y offset (+15) for proper centering
 */
export const RIGHT_CENTER_SQUARE_BOUNDS = {
  // Center path positions (RC1-RC6) - middle row going right to left
  RC1: { x: 466.43, y: 117.97, width: 56.11, height: 48.05 }, // Col 6 rightmost
  RC2: { x: 389.58, y: 117.97, width: 56.11, height: 48.05 }, // Col 5
  RC3: { x: 312.74, y: 117.97, width: 56.11, height: 48.05 }, // Col 4
  RC4: { x: 235.9, y: 117.97, width: 56.11, height: 48.05 }, // Col 3
  RC5: { x: 159.06, y: 117.97, width: 56.11, height: 48.05 }, // Col 2
  RC6: { x: 82.22, y: 117.97, width: 56.11, height: 48.05 }, // Col 1 leftmost

  // Start road positions (RS1-RS6) - bottom row going right to left
  RS1: { x: 466.43, y: 194.81, width: 56.11, height: 48.05 }, // Col 6 rightmost bottom
  RS2: { x: 389.58, y: 194.81, width: 56.11, height: 48.05 }, // Col 5 bottom (blank but mapped)
  RS3: { x: 312.74, y: 194.81, width: 56.11, height: 48.05 }, // Col 4 bottom
  RS4: { x: 235.9, y: 194.81, width: 56.11, height: 48.05 }, // Col 3 bottom
  RS5: { x: 159.06, y: 194.81, width: 56.11, height: 48.05 }, // Col 2 bottom
  RS6: { x: 82.22, y: 194.81, width: 56.11, height: 48.05 }, // Col 1 leftmost bottom

  // Final road positions (RF1-RF6) - top row going right to left
  RF1: { x: 466.43, y: 41.13, width: 56.11, height: 48.05 }, // Col 6 rightmost top
  RF2: { x: 389.58, y: 41.13, width: 56.11, height: 48.05 }, // Col 5 top
  RF3: { x: 312.74, y: 41.13, width: 56.11, height: 48.05 }, // Col 4 top
  RF4: { x: 235.9, y: 41.13, width: 56.11, height: 48.05 }, // Col 3 top
  RF5: { x: 159.06, y: 41.13, width: 56.11, height: 48.05 }, // Col 2 top
  RF6: { x: 82.22, y: 41.13, width: 56.11, height: 48.05 }, // Col 1 leftmost top

  // Final winning position (RFINAL) - arrow/triangle at left
  RFINAL: { x: 0, y: 126.99, width: 56.11, height: 48.05 } // Left triangle center
} as const;

/**
 * Left Road Section (Yellow)
 * Layout: YS (top), YC (middle), YF (bottom), left to right
 * Adjusted with x offset (-145) and y offset (+10) for proper centering
 */
export const LEFT_CENTER_SQUARE_BOUNDS = {
  // Center path positions (YC1-YC6) - middle row going left to right
  YC1: { x: -32.36, y: 122.97, width: 56.11, height: 48.05 }, // Col 1 leftmost
  YC2: { x: 44.48, y: 122.97, width: 56.11, height: 48.05 }, // Col 2
  YC3: { x: 121.32, y: 122.97, width: 56.11, height: 48.05 }, // Col 3
  YC4: { x: 198.16, y: 122.97, width: 56.11, height: 48.05 }, // Col 4
  YC5: { x: 275, y: 122.97, width: 56.11, height: 48.05 }, // Col 5
  YC6: { x: 351.84, y: 122.97, width: 56.11, height: 48.05 }, // Col 6 rightmost

  // Start road positions (YS1-YS6) - top row going left to right, YS1 is LEFTMOST
  YS1: { x: -32.36, y: 46.13, width: 56.11, height: 48.05 }, // Col 1 leftmost top
  YS2: { x: 44.48, y: 46.13, width: 56.11, height: 48.05 }, // Col 2 top (blank but mapped)
  YS3: { x: 121.32, y: 46.13, width: 56.11, height: 48.05 }, // Col 3 top
  YS4: { x: 198.16, y: 46.13, width: 56.11, height: 48.05 }, // Col 4 top
  YS5: { x: 275, y: 46.13, width: 56.11, height: 48.05 }, // Col 5 top
  YS6: { x: 351.84, y: 46.13, width: 56.11, height: 48.05 }, // Col 6 rightmost top

  // Final road positions (YF1-YF6) - bottom row going left to right
  YF1: { x: -32.36, y: 199.81, width: 56.11, height: 48.05 }, // Col 1 leftmost bottom
  YF2: { x: 44.48, y: 199.81, width: 56.11, height: 48.05 }, // Col 2 bottom
  YF3: { x: 121.32, y: 199.81, width: 56.11, height: 48.05 }, // Col 3 bottom
  YF4: { x: 198.16, y: 199.81, width: 56.11, height: 48.05 }, // Col 4 bottom
  YF5: { x: 275, y: 199.81, width: 56.11, height: 48.05 }, // Col 5 bottom
  YF6: { x: 351.84, y: 199.81, width: 56.11, height: 48.05 }, // Col 6 rightmost bottom

  // Final winning position (YFINAL) - arrow/triangle at right
  YFINAL: { x: 435, y: 122.97, width: 56.11, height: 48.05 } // Right triangle center
} as const;
